import type { SimpleSelectOptions } from "@/modules/uiKitOverrides/SelectFilter/Select"
import type { ChangeEvent, FocusEventHandler, FunctionComponent } from "react"

import classNames from "classnames"
import _toString from "lodash/toString"
import { useEffect, useState } from "react"

import { Cross } from "@jobteaser/spark/components/Icons/Cross"
import { Globe } from "@jobteaser/spark/components/Icons/Globe"
import { LocationOutlined } from "@jobteaser/spark/components/Icons/LocationOutlined"

import { DropdownContent } from "@/modules/jobAds/client/components/AlgoliaLocationFilter/DropdownContent/DropdownContent"
import { DEFAULT_RADIUS_KM } from "@/modules/jobAds/client/constants"
import { useJobAdsFilterNavigation } from "@/modules/jobAds/client/hooks/useJobAdsFilterNavigation"
import { useJobAdsSearchParams } from "@/modules/jobAds/client/hooks/useJobAdsSearchParams"
import { useLocationFilter } from "@/modules/jobAds/client/hooks/useLocationFilter"
import { getJobAdsSearchParamsFromURLSearchParams } from "@/modules/jobAds/client/utils/getJobAdsSearchParamsFromURLSearchParams"
import { Dropdown } from "@/modules/layouts/components/Dropdown/Dropdown"
import {
  computeLocationObjectFromSingleQueryParam,
  computeLocationQueryParamFromObject,
  getDisplayableLocation,
} from "@/modules/locations/locations"

import styles from "./LocationFilter.module.css"

export type LocationChangeArgs = {
  isLocationACity: boolean
}

type LocationFilterProps = {
  abroadOnlyLabel: string
  clearLabel: string
  enableAbroadOption: boolean
  onLocationChange: (args: LocationChangeArgs) => void
  placeholder: string
}

export const LocationFilter: FunctionComponent<LocationFilterProps> = ({
  abroadOnlyLabel,
  clearLabel,
  enableAbroadOption,
  onLocationChange,
  placeholder,
}) => {
  const searchParams = useJobAdsSearchParams()
  const { navigateWithUpdatedQueryParams } = useJobAdsFilterNavigation()
  const { location, isAbroadOnly, lat, lng, radius, localizedLocation } =
    getJobAdsSearchParamsFromURLSearchParams(searchParams)
  const locationObject = location ? computeLocationObjectFromSingleQueryParam(location, localizedLocation) : null
  const isASavedSearch = searchParams.has("saved_search")
  const locationLabel = isAbroadOnly ? abroadOnlyLabel : getDisplayableLocation(locationObject)

  const [currentInput, setCurrentInput] = useState(locationLabel)

  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const isAlgoliaLocationRetrieved = currentInput && lat && lng

  const [displayClearIcon, setDisplayClearIcon] = useState(isAlgoliaLocationRetrieved || isAbroadOnly)
  const [suggestedOptions, setSuggestedOptions] = useState<SimpleSelectOptions[]>([])
  const [isLocationACity, setIsLocationACity] = useState(!!locationObject?.city)
  const [selectedLocation, setSelectedLocation] = useState<
    (SimpleSelectOptions & { lat: string | null; lng: string | null }) | undefined
  >({
    label: currentInput,
    lat: lat || null,
    lng: lng || null,
    value: computeLocationQueryParamFromObject(locationObject || {}),
  })
  const [isAbroadOnlyOptionSelected, setAbroadOnlyOptionSelected] = useState<boolean>(isAbroadOnly)
  const [filterReady, setFilterReady] = useState<boolean>(false)

  const locationFilterHook = useLocationFilter()

  const { fetchPredictionsDebounced, fetchDetails } = locationFilterHook(
    process.env.NEXT_PUBLIC_WOOSMAP_JOB_SEARCH_FO_PUBLIC || ""
  )

  const displayPinIcon = !isAbroadOnlyOptionSelected || currentInput !== abroadOnlyLabel

  const handleFocus: FocusEventHandler<HTMLInputElement> = (event): void => {
    event.stopPropagation()
    setTimeout(() => {
      setIsOpen(true)
      if (isAbroadOnlyOptionSelected) {
        setCurrentInput("")
      }
    }, 200)
  }

  const handleClose = (): void => {
    if (!selectedLocation || !selectedLocation.label) {
      if (isAbroadOnlyOptionSelected) {
        setCurrentInput(abroadOnlyLabel)
      } else {
        setCurrentInput("")
      }
    } else {
      setDisplayClearIcon(true)
      setCurrentInput(selectedLocation.label)
    }
    setSuggestedOptions([])
    setHasError(false)
    setIsOpen(false)
  }

  const handleChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>): void => {
    if (!isOpen) {
      setIsOpen(true)
    }

    setIsLoading(value !== "")
    setSuggestedOptions([])
    setHasError(false)
    setCurrentInput(value)
    setDisplayClearIcon(false)
    fetchPredictionsDebounced(value, {
      onError: () => {
        setSuggestedOptions([])
        setHasError(true)
      },
      onSettled: () => {
        setIsLoading(false)
        setFilterReady(true)
      },
      onSuccess: predictions => {
        setSuggestedOptions(predictions)
      },
      onZeroResult: () => setSuggestedOptions([]),
    })
  }

  const handleClear = (): void => {
    setCurrentInput("")
    setSuggestedOptions([])
    setSelectedLocation(undefined)
    setDisplayClearIcon(false)
    setAbroadOnlyOptionSelected(false)
    setFilterReady(true)
    setIsLocationACity(false)
  }

  const handleOptionClick = ({ label, value }: SimpleSelectOptions): void => {
    fetchDetails(value, {
      onError: () => {
        setHasError(true)
      },
      onSuccess: locationResult => {
        setCurrentInput(label)
        setSuggestedOptions([])

        setSelectedLocation({
          label,
          lat: _toString(locationResult.latitude),
          lng: _toString(locationResult.longitude),
          value: computeLocationQueryParamFromObject(locationResult),
        })
        setIsOpen(false)
        setDisplayClearIcon(true)
        setAbroadOnlyOptionSelected(false)
        setFilterReady(true)
        setIsLocationACity(!!locationResult.city)
      },
    })
  }

  const handleAbroadOptionClick = (): void => {
    setCurrentInput(abroadOnlyLabel)
    setSuggestedOptions([])
    setSelectedLocation(undefined)
    setIsOpen(false)
    setDisplayClearIcon(true)
    setAbroadOnlyOptionSelected(true)
    setFilterReady(true)
    setIsLocationACity(false)
  }

  useEffect(() => {
    if (currentInput !== locationLabel) {
      setCurrentInput(locationLabel)
      setDisplayClearIcon(true)
    }
    // we want to update the currentInput only when the update coming from a click on a saved search
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isASavedSearch])

  useEffect(() => {
    if (filterReady) {
      const doesGeoSearchExist = selectedLocation?.lat && selectedLocation?.lng
      const currentRadius = isLocationACity && (radius || DEFAULT_RADIUS_KM)

      navigateWithUpdatedQueryParams({
        availableFilters: {
          ...(enableAbroadOption && { abroad_only: isAbroadOnlyOptionSelected.toString() }),
          lat: selectedLocation?.lat || "",
          lng: selectedLocation?.lng || "",
          localized_location: selectedLocation?.label,
          location: selectedLocation?.value || "",
          radius: doesGeoSearchExist && currentRadius ? currentRadius.toString() : "",
        },
      })

      setFilterReady(false)
    }
    // we want to update the search params only when we decided (when filterReady is true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterReady])

  useEffect(() => {
    setIsLocationACity(!!locationObject?.city)
  }, [locationObject?.city])

  useEffect(() => {
    onLocationChange({ isLocationACity })
  }, [onLocationChange, isLocationACity])

  return (
    <div
      className={classNames(styles.main, {
        [styles["main--radius-enabled"]]: isLocationACity,
      })}
    >
      <div className={styles.main__inputContainer}>
        {displayPinIcon ? (
          <LocationOutlined className={styles.main__pinIcon} />
        ) : (
          <Globe className={styles.main__pinIcon} />
        )}
        <input
          className={styles.main__input}
          id="location-filter"
          name="location-filter"
          onClick={event => event.stopPropagation()}
          onChange={handleChange}
          onFocus={handleFocus}
          type="text"
          value={currentInput}
          placeholder={placeholder}
          autoComplete="off"
        />
        {displayClearIcon && (
          <button
            aria-label={clearLabel}
            type="button"
            className={styles.main__clearButton}
            onClick={handleClear}
            data-testid="location-clear-icon"
          >
            <Cross className={styles.main__clearIcon} size={20} />
          </button>
        )}
      </div>
      {isOpen && (
        <Dropdown onClose={handleClose}>
          <DropdownContent
            hasError={hasError}
            isLoading={isLoading}
            currentInput={currentInput}
            suggestedOptions={suggestedOptions}
            selectedLocation={selectedLocation}
            onOptionClick={handleOptionClick}
            enableAbroadOption={enableAbroadOption}
            onAbroadOptionClick={handleAbroadOptionClick}
          />
        </Dropdown>
      )}
    </div>
  )
}
